"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { abonneerFilmStand, bewegingIsUit, geefFilmPlek, meldDealFilm, vraagFilmPlek } from "@/lib/dealfilm";

// Stille film over de foto van een dealkaart (Dennis 26-10-2026). De foto
// blijft het LCP-element: de `src` komt pas na `load` en als de kaart voor
// minstens de helft in beeld is. Alles behalve een werkende film = alleen foto.

/** Mag er een film? Niet bij reduced-motion, saveData of een 2g-lijn. */
export function magKaartFilm() {
  if (typeof window === "undefined") return true;
  try {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    const c =window.navigator && window.navigator.connection;
    if (c && c.saveData) return false;
    if (c && typeof c.effectiveType === "string" && c.effectiveType.includes("2g")) return false;
  } catch {
    return false;
  }
  return true;
}

function naLoad(cb) {
  if (document.readyState === "complete") {
    cb();
    return () => {};
  }
  window.addEventListener("load", cb, { once: true });
  return () => window.removeEventListener("load", cb);
}

// `meet` ({ dealsId, plek, groep }) alleen voor films uit het register: dan
// meldt de kaart dealfilm_getoond / dealfilm_fout in de dataLayer. Hoogstens
// MAX_KAARTFILMS_TEGELIJK registerfilms spelen tegelijk (de vaste lijst niet).
export default function KaartFilm({ mp4, poster, meet }) {
  // Server: film toegestaan; de client corrigeert na hydratie zonder effect-setState.
  // "Beweging uit" (FILM_UIT_KEY) stopt ook films die al spelen of later verschijnen.
  const mag = useSyncExternalStore(abonneerFilmStand, () => magKaartFilm() && !bewegingIsUit(), () => true);
  const [fout, setFout] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const plekRef = useRef({});
  const gemeldRef = useRef(false);
  const heeftMeet = !!meet;

  useEffect(() => {
    if (!mag || fout || typeof IntersectionObserver !== "function") return undefined;
    let io = null;
    const plek = plekRef.current;
    let gemonteerd = null;
    const stopWachten = naLoad(() => {
      const video = videoRef.current;
      if (!video) return;
      gemonteerd = video;
      io = new IntersectionObserver(
        (entries) => {
          const last = entries[entries.length - 1];
          const v = videoRef.current;
          if (!last || !v) return;
          try {
            // isIntersecting is al waar bij 1 pixel: de helft telt via de ratio.
            if (last.isIntersecting && last.intersectionRatio >= 0.5) {
              const speel = () => {
                try {
                  if (!v.getAttribute("src")) v.setAttribute("src", mp4);
                  const p = v.play();
                  if (p && p.catch) p.catch(() => {});
                } catch {
                  // autoplay geweigerd: foto blijft
                }
              };
              // Alleen registerfilms (`meet`) wachten op een plek; de vaste
              // homepagefilms spelen zonder limiet, zoals op main.
              if (heeftMeet) vraagFilmPlek(plek, speel);
              else speel();
            } else {
              v.pause();
              geefFilmPlek(plek);
            }
          } catch {
            // autoplay geweigerd: foto blijft
          }
        },
        { threshold: 0.5 },
      );
      io.observe(video.parentElement || video);
    });
    return () => {
      stopWachten();
      if (io) io.disconnect();
      // Film weg (reduced-motion, "Beweging uit"): eerst pauzeren, dan verdwijnt hij.
      try {
        if (gemonteerd) gemonteerd.pause();
      } catch {
        // niets te pauzeren
      }
      geefFilmPlek(plek);
    };
  }, [mag, fout, mp4, heeftMeet]);

  if (!mag || fout) return null;

  return (
    <video
      ref={videoRef}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      style={{ pointerEvents: "none", opacity: playing ? 1 : 0, transition: "opacity 600ms ease" }}
      onPlaying={() => {
        setPlaying(true);
        if (meet && !gemeldRef.current) {
          gemeldRef.current = true;
          meldDealFilm("getoond", meet);
        }
      }}
      onError={() => {
        setFout(true);
        if (meet && !gemeldRef.current) {
          gemeldRef.current = true;
          meldDealFilm("fout", meet);
        }
      }}
    />
  );
}
